import { useState, useEffect, useContext } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import formatDate from "../../../components/utils/DateFormatter";
import Spinner from "../../spinner/Spinner";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../../api/ApiServices";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import CustomStyles from "../../../components/utils/CustomStyles";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import AxiosApiCall from "../../../api/AxiosApiCall";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import { ReactComponent as ArrowLeftSquare } from "bootstrap-icons/icons/arrow-left-square-fill.svg";

const WalletsHistory = () => {
    const [histories, setHistories] = useState([]);
    const [walletSummary, setWalletSummary] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingFilter, setLoadingFilter] = useState(false);
    const [branch_Id, setBranchId] = useState("")
    const [branchName, setBranchName] = useState("")
    const [filterText, setFilterText] = useState("");
    const [pagination, setPagination] = useState({
        page: 1,
        per_page: 20,
        total: 0,
    });
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [confirmationLoading, setConfirmationLoading] = useState(false);
    const { permissions } = useContext(AuthContext);

    const [filters, setFilters] = useState({
        branch_id: "",
        per_page: 20,
        page: 1,
        wallet_type: "",
        type: "",
        start_date: "",
        end_date: "",
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { data } = location.state || {};

    //axios api encryption call
    const axios = AxiosApiCall();

    useEffect(() => {
        if (data) {
            // console.log("Branch data:", data);
            setBranchName(data.name || "");
            setBranchId(data.id || "");
            // Update filters with branch_id
            setFilters(prev => ({
                ...prev,
                branch_id: data.id || ""
            }));
        }
    }, [data]);

    const fetchAllWalletsHistory = async () => {
        if (!branch_Id && !filters.branch_id) {
            // console.log("No branch ID available");
            return;
        }

        const formData = {
            branch_id: branch_Id || filters.branch_id,
            per_page: filters.per_page,
            page: filters.page,
            wallet_type: filters.wallet_type,
            type: filters.type,
            start_date: filters.start_date,
            end_date: filters.end_date,
        };

        // console.log("Fetching with filters:", formData);
        setLoading(true);

        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;

            if (!token) {
                throw new Error("No access token found.");
            }

            const response = await axios.get(ApiServices.getAllWalletHistory, {
                params: formData,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                withCredentials: true,
            });

            // console.log("API response:", response);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                setHistories(responseData.histories || []);

                const summary = responseData.wallet_summary || {};
                // console.log("summary", summary);
                setWalletSummary({
                    account_name: summary.account_name ?? "NOT AVAILABLE",
                    account_number: summary.account_number ?? "NOT AVAILABLE",
                    main_balance: summary.main_balance ? parseFloat(summary.main_balance.replace(/,/g, "")) : 0,
                    incentive_balance: summary.incentive_balance ? parseFloat(summary.incentive_balance.replace(/,/g, "")) : 0,
                    expense_balance: summary.expense_balance ? parseFloat(summary.expense_balance.replace(/,/g, "")) : 0,
                    float_balance: summary.float_balance ? parseFloat(summary.float_balance.replace(/,/g, "")) : 0,
                    total_balance: summary.total_balance ? parseFloat(summary.total_balance) : 0,
                });

                setPagination({
                    page: responseData.page || 1,
                    per_page: responseData.per_page || 20,
                    total: responseData.total || 0,
                });
            }

            else {
                handleError({ response: { status, data: { message } } }, navigate);
            }
        } catch (error) {
            console.error("Error fetching wallet history:", error);
            handleError(error, navigate);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when branch_Id or filters change
    useEffect(() => {
        if (branch_Id || filters.branch_id) {
            fetchAllWalletsHistory();
        }
    }, [branch_Id, filters.branch_id, filters.page, filters.per_page]);

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handlePerRowsChange = (newPerPage, newPage) => {
        setFilters(prev => ({ ...prev, per_page: newPerPage, page: newPage }));
        setPagination(prev => ({ ...prev, per_page: newPerPage, page: newPage }));
    };



    const handleApplyFilters = async () => {
        setLoadingFilter(true);
        try {
            // Reset to page 1 when applying filters
            setFilters((prev) => ({ ...prev, page: 1 }));

            await fetchAllWalletsHistory();
        } catch (error) {
            console.error("Error applying filters:", error);
        } finally {
            setLoadingFilter(false);
        }
    };


    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };



    const handleFilter = (e) => {
        setFilterText(e.target.value);
        setResetPaginationToggle(!resetPaginationToggle);
    };

    // Define Expandable Component for Description
    const ExpandableComponent = ({ data }) => {
        return (
            <div className="p-4 bg-gray-50 ">
                <div className="mb-2">
                    <span className="font-semibold text-gray-700">Description:</span>
                </div>
                <div className="text-gray-600 bg-white p-3 ">
                    {data.description || "No description available"}
                </div>
            </div>
        );
    };

    const columns = [
        { name: "Date", selector: (row) => formatDate(row.created_at) ? row.created_at : "NOT AVAILABLE", width: "180px", sortable: true },
        { name: "Reference", selector: (row) => row.reference ? row.reference : "NOT AVAILABLE", width: "180px", sortable: true },
        {
            name: "Type",
            selector: (row) => row.type ? row.type : "NOT AVAILABLE",
            sortable: true,
            cell: (row) => (
                <span className={`px-2 py-1 rounded text-sm font-medium ${row.type === 'credit'
                    ? 'bg-darkGreen3 text-lGrey'
                    : 'bg-dash3 text-lGrey'
                    }`}>
                    {row.type === 'credit' ? 'Credit' : 'Debit'}
                </span>
            )
        },
        { name: "Wallet", selector: (row) => row.wallet_type ? row.wallet_type.toUpperCase() : "NOT AVAIABLE", sortable: true },
        {
            name: "Amount",
            selector: (row) => row.amount ? CurrencyFormatter(row.amount) : "0:00",
            sortable: true,
            width: "200px"
            // cell: (row) => (
            //     <span className={row.type === 'credit' ? 'text-darkGreen3' : 'text-dash3'}>
            //         <CurrencyFormatter value={row.amount} />
            //     </span>
            // ),
        },
        {
            name: "Balance Before",
            selector: (row) => row.balance_before ? CurrencyFormatter(row.balance_before) : "0.00", width: "180px",
            // cell: (row) => <CurrencyFormatter value={row.balance_before} />,
        },
        {
            name: "Balance After",
            selector: (row) => row.balance_after ? CurrencyFormatter(row.balance_after) : "0:00", width: "180px",
            // cell: (row) => <CurrencyFormatter value={row.balance_after} />,
        },
        // { name: "Time Ago", selector: (row) => row.time_ago ? row.time_ago : "NOT AVAILABLE", width: "200px", },
    ];

    const filteredItems = histories.filter((item) =>
        Object.keys(item).some((key) =>
            item[key]?.toString().toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const transactionData = filteredItems.map((history) => ({
        id: history.id,
        amount: history.amount,
        type: history.type,
        wallet_type: history.wallet_type,
        balance_before: history.balance_before,
        balance_after: history.balance_after,
        reference: history.reference || "N/A",
        description: history.description || "N/A",
        created_at: history.created_at,
        time_ago: history.time_ago,
    }));

    const sortedData = [...transactionData].sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA;
    });

    return (
        <div>
            <MainLayouts>
                <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                    <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
                        <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                            <ol className="list-none p-0 inline-flex mb-2">
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                        Operations
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                        Branch
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                        Wallets
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-orange1">History</p>
                                </li>
                            </ol>
                        </nav>
                    </section>

                    {/* Branch Info and Search - Side by side on desktop, stacked on mobile */}
                    <div className="pb-4 bg-white dark:bg-gray-900 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                        {/* Branch Info Display */}
                        {branchName && (
                            <div className="bg-gray-50 rounded-lg shadow-sm p-4 lg:w-1/2">
                                <h2 className="font-bold text-lg mb-1 text-darkGreen3 capitalize">{branchName}</h2>
                            </div>
                        )}

                        {/* Search Input */}
                        <div className="relative lg:w-1/2">
                            <input
                                type="search"
                                className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                placeholder="Search transactions..."
                                value={filterText}
                                onChange={handleFilter}
                            />
                        </div>
                        <Link
                            to="/operations/branch/list"
                            className="mx-2 w-full sm:w-auto text-dGrey3 bg-darkGreen3 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                        >
                            <span className="flex justify-center sm:justify-start mx-2">
                                <ArrowLeftSquare className="mr-2" />
                                Back
                            </span>
                        </Link>
                    </div>

                    {/* Filters */}
                    <div className="pb-4 bg-white dark:bg-gray-900 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                        <div>
                            <label className="block text-sm font-medium mb-1">Wallet Type</label>
                            <select
                                value={filters.wallet_type}
                                onChange={(e) => handleFilterChange('wallet_type', e.target.value)}
                                className="w-full border border-lGrey p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All Wallet Types</option>
                                <option value="main">Main</option>
                                <option value="incentive">Incentive</option>
                                <option value="float">Float</option>
                                {/* <option value="expense">Expense</option> */}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Transaction Type</label>
                            <select
                                value={filters.type}
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                                className="w-full border border-lGrey p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All Types</option>
                                <option value="credit">Credit</option>
                                <option value="debit">Debit</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Start Date</label>
                            <input
                                type="date"
                                value={filters.start_date}
                                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                                className="w-full border border-lGrey p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">End Date</label>
                            <input
                                type="date"
                                value={filters.end_date}
                                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                                className="w-full border border-lGrey p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <button
                                onClick={handleApplyFilters}
                                disabled={loadingFilter}
                                className="w-full bg-darkGreen3 text-lGrey px-4 py-2 rounded hover:bg-darkGreen3 disabled:bg-gray-400 flex items-center justify-center gap-2"
                            >
                                {loadingFilter ? (
                                    <>
                                        Filtering...
                                    </>
                                ) : (
                                    "Apply Filters"
                                )}
                            </button>
                        </div>

                    </div>

                    {/* Wallet Summary */}
                    {walletSummary && Object.keys(walletSummary).length > 0 && (



                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="p-4 bg-blue-50 rounded-lg shadow">
                                <p className="text-sm text-darkGreen3">Main Balance</p>
                                <p className="font-bold text-lg">
                                    {CurrencyFormatter(walletSummary.main_balance)}
                                </p>
                            </div>
                            <div className="p-4 bg-green-50 rounded-lg shadow">
                                <p className="text-sm text-darkGreen3">Incentive Balance</p>
                                <p className="font-bold text-lg">
                                    {CurrencyFormatter(walletSummary.incentive_balance)}
                                </p>
                            </div>
                            <div className="p-4 bg-red-50 rounded-lg shadow">
                                <p className="text-sm text-darkGreen3">Expense Balance</p>
                                <p className="font-bold text-lg">
                                    {CurrencyFormatter(walletSummary.expense_balance)}
                                </p>
                            </div>
                            <div className="p-4 bg-purple-50 rounded-lg shadow">
                                <p className="text-sm text-darkGreen3">Total Balance</p>
                                <p className="font-bold text-lg">
                                    {CurrencyFormatter(walletSummary.total_balance)}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Data Table */}
                    {loading ? (
                        <Spinner />
                    ) : (
                        <DataTable
                            columns={columns}
                            data={sortedData}
                            pagination
                            paginationServer
                            paginationTotalRows={pagination.total}
                            paginationDefaultPage={pagination.page}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            highlightOnHover
                            striped
                            customStyles={CustomStyles}
                            noDataComponent={
                                <div className="text-center py-4">
                                    <p>No wallet history found</p>
                                    {filterText && <p className="text-sm text-gray-500">Try adjusting your search or filters</p>}
                                </div>
                            }
                            expandableRows
                            expandableRowsComponent={ExpandableComponent}
                        />
                    )}
                </div>
            </MainLayouts>
        </div>
    );
};

export default WalletsHistory;